import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Calendar, Clock, Newspaper } from 'lucide-react'
import { BLOG_POSTS } from '../data/blogPosts'
import { blogPath } from '../lib/localizedUrl'
import bpEn from '../data/blogPosts.en.json'
import bpDe from '../data/blogPosts.de.json'
import bpPl from '../data/blogPosts.pl.json'
import bpSk from '../data/blogPosts.sk.json'
import bpIt from '../data/blogPosts.it.json'
import bpHu from '../data/blogPosts.hu.json'
import bpUk from '../data/blogPosts.uk.json'
import bpEs from '../data/blogPosts.es.json'
import bpFr from '../data/blogPosts.fr.json'
import bpNl from '../data/blogPosts.nl.json'
import bpJa from '../data/blogPosts.ja.json'

const TRANS: Record<string, any[]> = { en: bpEn, de: bpDe, pl: bpPl, sk: bpSk, it: bpIt, hu: bpHu, uk: bpUk, es: bpEs, fr: bpFr, nl: bpNl, ja: bpJa }

/** Sekce „Nejnovější z blogu" na homepage — interní prolinkování + signál čerstvosti. */
export default function LatestPosts() {
  const { t, i18n } = useTranslation()
  const tr = TRANS[i18n.language] || (i18n.language !== 'cs' && TRANS.en) || []
  const localize = (p: typeof BLOG_POSTS[number]) => {
    const x = tr.find((y) => y.slug === p.slug)
    return x ? { ...p, ...x } : p
  }
  const posts = [...BLOG_POSTS].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3).map(localize)

  return (
    <section className="max-w-6xl mx-auto px-4 py-20">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary-600 mb-2">
            <Newspaper className="w-4 h-4" />{t('blog.title', { defaultValue: 'Blog' })}
          </p>
          <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
            {t('blog.latest_title', { defaultValue: 'Nejnovější články' })}
          </h2>
        </div>
        <Link to="/blog" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:gap-2.5 transition-all">
          {t('blog.all', { defaultValue: 'Všechny články' })}<ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {posts.map((p) => (
          <Link key={p.slug} to={blogPath(p.slug, i18n.language)}
            className="block bg-white dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-white/10 p-6 hover:border-primary-300 hover:shadow-md transition group">
            <div className="flex flex-wrap gap-2 mb-3">
              {p.tags.slice(0, 2).map((tag: string) => (
                <span key={tag} className="px-2.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 text-xs font-semibold">{tag}</span>
              ))}
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-primary-600 transition leading-snug">{p.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed line-clamp-3">{p.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-gray-400 mt-4">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(p.date).toLocaleDateString('cs-CZ')}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{p.readMins} min</span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-6 text-center sm:hidden">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
          {t('blog.all', { defaultValue: 'Všechny články' })}<ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  )
}
